export interface TopicSection {
  heading: string;
  paragraphs: string[];
  quote?: string;
}

export interface Topic {
  id: number;
  slug: string;
  title: string;
  categoryId: string;
  excerpt: string;
  cover: string;
  author: string;
  date: string;
  readTime: number;
  tags: string[];
  hot: number;
  featured?: boolean;
  lead: string;
  takeaways: string[];
  sections: TopicSection[];
}

export const topics: Topic[] = [
  {
    id: 1,
    slug: "livestream-supply-chain",
    title: "一场带货风波的供应链起底：从爆单到退货潮的完整链条",
    categoryId: "business",
    excerpt:
      "当一场直播在四小时内冲上千万销售额，问题往往不在台前，而在那条被临时拉长的供应链上。我们把这场风波按时间线重新拆开。",
    cover:
      "https://readdy.ai/api/search-image?query=moody%20editorial%20abstract%20photograph%20of%20stacked%20cardboard%20shipping%20boxes%20in%20a%20dim%20warehouse%20with%20warm%20amber%20light%20and%20long%20shadows%2C%20cinematic%20depth%20of%20field%2C%20muted%20earthy%20tones%2C%20documentary%20style&width=1200&height=750&seq=kl-cover-livestream&orientation=landscape",
    author: "看料档案 · 商业组",
    date: "2026-08-28",
    readTime: 11,
    tags: ["直播电商", "供应链", "退货率"],
    hot: 9820,
    featured: true,
    lead:
      "这不是一次简单的「翻车」。从选品、备货、履约到售后，每一个环节都在这次风波里留下了一条清晰的痕迹。我们按时间顺序把它还原出来。",
    takeaways: [
      "爆单当天的备货缺口，早在两周前就已被写进供货方的排产表。",
      "退货率异常并非售后问题，而是履约时效被压缩后的连锁反应。",
      "真正被低估的环节是临时扩编的客服与仓储，它们决定了口碑的走向。",
    ],
    sections: [
      {
        heading: "节点一：被提前透支的产能",
        paragraphs: [
          "把时间拨回到活动开始前的第十六天，供货方的排产表上已经出现了一个远超日常值三倍的订单量。为了赶上节点，工厂选择了临时加班与外包代工并行的方式填补缺口。",
          "这个决定在当时看起来只是成本问题，但它直接决定了后续所有环节的容错空间。当实际销量进一步超出预估时，已经没有任何缓冲余地。",
        ],
        quote: "所有突发的品控问题，几乎都能在活动开始前的那张排产表里找到伏笔。",
      },
      {
        heading: "节点二：被压缩的履约时效",
        paragraphs: [
          "订单量冲高后，最先告急的不是商品，而是出库能力。为了维持「当日发」的承诺，仓库把原本需要抽检的流程改成了抽检与直发并行，这为后续的退换埋下了隐患。",
          "履约时效被打乱的另一个后果是物流信息的滞后。当消费者在页面上长时间看不到动态更新时，焦虑会直接转化为退款申请，而不是等待。",
        ],
      },
      {
        heading: "节点三：售后端的真实压力",
        paragraphs: [
          "很多分析把退货率归因于产品本身，但这次的性质不同。数据显示，超过六成的退货申请集中在发货后的第三天，恰好是物流信息最不透明的时段。",
          "也就是说，退货潮真正的触发点是「信息不确定」，而非成品缺陷。售后团队当时面对的不是质量投诉，而是大量询问与催单，这直接挤占了处理正常问题的人力。",
        ],
      },
      {
        heading: "复盘：一条供应链的三处脆弱点",
        paragraphs: [
          "如果把这次风波当作一个样本，它至少暴露了三处脆弱点：产能预估缺乏弹性、履约只保速度不保透明、售后预案没有按峰值配置。",
          "值得注意的是，这三处问题没有一个是「突然」出现的，它们都在事前的操作记录里留下了明确的先兆。这也是我们坚持做时间线梳理的原因——很多所谓的内幕，其实早就写在流程里。",
        ],
      },
    ],
  },
  {
    id: 2,
    slug: "recommendation-algorithm-review",
    title: "推荐算法调整复盘：一次流量分发规则变化引发的连锁反应",
    categoryId: "industry",
    excerpt:
      "一次看似只是参数微调的算法更新，为什么会让一部分创作者的数据在两周内集体腰斩？我们从规则、指标与内容生态三个层面拆开看。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20data%20visualization%20of%20glowing%20warm%20orange%20network%20nodes%20and%20connecting%20lines%20on%20a%20deep%20charcoal%20background%2C%20elegant%20minimal%20editorial%20illustration%2C%20soft%20glow%2C%20high%20contrast&width=1200&height=750&seq=kl-cover-algorithm&orientation=landscape",
    author: "看料档案 · 行业组",
    date: "2026-08-19",
    readTime: 9,
    tags: ["推荐算法", "流量分发", "创作者生态"],
    hot: 8630,
    featured: true,
    lead:
      "算法从不解释自己。但当大量创作者同时感受到数据的异动时，规则的变化就会留下可以被观察的痕迹。我们试图把这些痕迹整理成一条推理链。",
    takeaways: [
      "这次调整的核心变化之一，是短期互动权重向完播率与复访率倾斜。",
      "受冲击最明显的，是高度依赖单一爆款的账号类型。",
      "生态层面的影响，往往要等两到三周才会在数据上完整显现。",
    ],
    sections: [
      {
        heading: "变化是从哪一周开始的",
        paragraphs: [
          "把参与调研的三十余个账号数据按周排列后，可以清楚地看到一条分界线：调整后的第二周，账号间的数据方差明显扩大。也就是说，同样的内容质量，差距被拉大了。",
          "这说明变化的并不是流量总量，而是流量的分配逻辑。理解这一点，是理解后续所有反应的前提。",
        ],
      },
      {
        heading: "被重新排序的指标",
        paragraphs: [
          "从创作者后台可见的指标口径推断，这次调整很可能提高了两个指标的权重：内容的完播率，以及读者的回流比例。相比之下，单纯的点击与点赞被相对弱化。",
          "这一变化的直接结果是，擅长制造「标题悬念」的内容获得了初始曝光，却难以完成二次分发；而结构完整、能让人看完的内容则获得了更长的分发周期。",
        ],
        quote: "当算法更看重「看完」，靠悬念拉来的点击就成了一次性消耗品。",
      },
      {
        heading: "谁被冲击，谁被利好",
        paragraphs: [
          "受冲击最明显的是高度依赖单条爆款的账号。这类账号的流量结构本就脆弱，一旦初始分发逻辑改变，缺乏稳定内容库的劣势会被迅速放大。",
          "相对的，拥有固定栏目与稳定更新节奏的账号获得了利好。算法偏好的变化，实际上奖励了「可预期」的内容供给。",
        ],
      },
      {
        heading: "生态影响的时间差",
        paragraphs: [
          "值得注意的是，规则调整与生态变化之间存在明显时间差。创作者的内容策略调整通常需要两到三周，因此初期的数据波动并不能代表最终结果。",
          "这也是判断算法影响的难点：它不制造剧烈的瞬间变化，而是通过持续筛选，缓慢地把生态推向新的均衡。",
        ],
      },
    ],
  },
  {
    id: 3,
    slug: "rumor-propagation-path",
    title: "一条行业传闻如何跑遍全网：信息发酵的六个关键节点",
    categoryId: "opinion",
    excerpt:
      "从某个匿名帖子里的一句话，到登上主流讨论，这条传闻只用了不到七十二小时。它经过了哪里，又被谁改写了含义？",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20editorial%20image%20of%20overlapping%20translucent%20paper%20sheets%20with%20faint%20text%20texture%20floating%20in%20warm%20dim%20light%2C%20muted%20sepia%20and%20amber%20tones%2C%20artistic%20blur%20and%20depth&width=1200&height=750&seq=kl-cover-rumor&orientation=landscape",
    author: "看料档案 · 舆情组",
    date: "2026-08-11",
    readTime: 8,
    tags: ["舆情深挖", "信息扩散", "传播路径"],
    hot: 7210,
    lead:
      "一次信息发酵，很少是线性的。我们把这条传闻的扩散过程切成六个节点，你会发现每一次转发，其实都在悄悄改写它的含义。",
    takeaways: [
      "信息的每一次跨平台迁移，都会丢失一部分限定条件。",
      "情绪化标题的加入，通常发生在传播的第三个节点之后。",
      "当讨论进入「立场先行」阶段，原始信息已基本失去纠错能力。",
    ],
    sections: [
      {
        heading: "起点：一句没有上下文的陈述",
        paragraphs: [
          "这条传闻最初出现在一个垂直讨论区，措辞相当克制，还附带了一句「仅为个人观察，未经核实」。但在第一次被转载时，这句限定条件就被删掉了。",
          "于是从第二个节点开始，它就从一个「观察」变成了一条「消息」。这个转变，是整个事件最重要的分水岭。",
        ],
      },
      {
        heading: "加速：跨平台迁移中的损耗",
        paragraphs: [
          "当信息从垂类社区迁移到泛内容平台时，为了适配更短的阅读习惯，标题被大幅压缩。压缩掉的恰恰是那些限定词与程度副词。",
          "我们比对了七个平台的首发版本，发现限定条件在迁移中平均丢失了约四成。信息没有变多，确定性却被凭空提高了。",
        ],
        quote: "传播的加速，往往是以牺牲准确性为代价换来的。",
      },
      {
        heading: "失控：立场先行的阶段",
        paragraphs: [
          "进入第四个节点后，讨论的重心已经从「这件事是否属实」转向了「这件事说明了什么」。一旦进入这个阶段，原始信息是否被核实，已经不再影响传播。",
          "这也是所有信息发酵事件的共同难点：当讨论服务于立场时，纠错所需的成本会远高于传播本身。",
        ],
      },
      {
        heading: "沉淀：留给读者的三个提醒",
        paragraphs: [
          "复盘这条路径，我们能得到的提醒其实很朴素：看清限定条件、区分观察与结论、警惕被压缩后的标题。",
          "舆情深挖的意义不在于评判某一方，而在于让传播的机制变得可见。看得见机制，才不容易被机制带着走。",
        ],
      },
    ],
  },
  {
    id: 4,
    slug: "franchise-expansion-ledger",
    title: "加盟潮退去之后的账本：连锁扩张被忽略的三项成本",
    categoryId: "business",
    excerpt:
      "开店速度一度被视为增长指标，但当扩张放缓，隐藏在加盟合同里的成本才开始显现。我们拆解了三项最容易被低估的支出。",
    cover:
      "https://readdy.ai/api/search-image?query=minimal%20editorial%20photo%20of%20a%20closed%20storefront%20with%20warm%20afternoon%20light%20casting%20long%20shadows%2C%20muted%20terracotta%20and%20cream%20tones%2C%20quiet%20melancholic%20mood%2C%20artistic%20composition&width=1200&height=750&seq=kl-cover-franchise&orientation=landscape",
    author: "看料档案 · 商业组",
    date: "2026-07-30",
    readTime: 10,
    tags: ["连锁加盟", "扩张成本", "门店经济"],
    hot: 6480,
    lead:
      "扩张期的账本往往好看，因为它把成本推迟到了未来。当扩张放缓，被推迟的部分就会集中出现。这篇文章只做一件事：把这三项成本摊开。",
    takeaways: [
      "装修与设备的折旧周期，普遍短于合同约定的合作年限。",
      "总部的供货价格并非恒定，调价条款常被写在附件里。",
      "最后一公里配送成本，是决定单店盈亏的关键变量。",
    ],
    sections: [
      {
        heading: "成本一：被低估的装修折旧",
        paragraphs: [
          "加盟合同普遍按三到五年签署，但门店装修与核心设备的实际经济寿命往往只有两年左右。这意味着在合同的后半段，门店要承担一次并未被计入初始预算的翻新支出。",
          "很多经营者在签约时只看了初始投入，却忽略了这份「中期账单」。它不会出现在开店测算表的第一页。",
        ],
      },
      {
        heading: "成本二：供货价的浮动空间",
        paragraphs: [
          "供应链是连锁体系的核心，也是利润分配的关键。多数合同会约定「总部有权根据市场情况调整供货价格」，这句话的弹性，往往在经营的中后期才被真切感受到。",
          "当单店销量下滑，而供货价保持刚性时，毛利会被同时从两端挤压。这也是很多门店「越做越薄」的直接原因。",
        ],
        quote: "合同里最值钱的从来不是金额，而是那些看起来无害的弹性条款。",
      },
      {
        heading: "成本三：最后一公里的隐性支出",
        paragraphs: [
          "对于以外卖为主的门店，配送成本常常被严重低估。平台抽成、履约服务费与高峰期溢价叠加后，实际到手的比例可能远低于测算值。",
          "这三项成本叠加，构成了扩张退潮期最真实的经营图景。理解了它们，才能理解为什么「开店速度」从来不是衡量连锁健康度的唯一指标。",
        ],
      },
    ],
  },
  {
    id: 5,
    slug: "membership-pricing-ladder",
    title: "会员制背后的定价阶梯：分账逻辑是怎么被设计的",
    categoryId: "industry",
    excerpt:
      "同样的商品，为什么在不同等级的会员手里价格差异如此明显？这背后不是随机的折扣，而是一套精密的定价阶梯。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20clean%20editorial%20image%20of%20ascending%20stacked%20geometric%20steps%20in%20cream%20and%20terracotta%2C%20soft%20directional%20light%2C%20minimalist%20architectural%20feel%2C%20warm%20muted%20palette&width=1200&height=750&seq=kl-cover-membership&orientation=landscape",
    author: "看料档案 · 行业组",
    date: "2026-07-22",
    readTime: 8,
    tags: ["会员制", "定价策略", "分账"],
    hot: 5920,
    lead:
      "会员体系从来不只是「回馈用户」。它更像一套筛选机制，用价格把不同付费意愿的人群分层，再针对每一层设计不同的收益模型。",
    takeaways: [
      "等级之间的价格差，实际对应的是不同的复购预期，而非不同的成本。",
      "高等级会员的让利，往往通过限定品类与使用门槛收回。",
      "定价阶梯的真正目标，是把用户从「随机购买」推向「习惯性购买」。",
    ],
    sections: [
      {
        heading: "阶梯的第一层：拉新的让利",
        paragraphs: [
          "最基础的会员等级通常让利幅度最大，目的也很直接：把一次性消费者转化为可识别、可触达的账户。这一层的让利，本质上是获客成本。",
          "理解这一点，就能理解为什么首单优惠总是格外慷慨，而续费优惠却逐年收窄。",
        ],
      },
      {
        heading: "阶梯的中间层：复购的锁定",
        paragraphs: [
          "中间等级的设计目标不是获客，而是复购。它通常通过限时权益、积分有效期与专属品类来制造「不使用就浪费」的心理。",
          "这一层的让利看似稳定，但通过限定品类与门槛，平台实际支付的边际成本远低于标称折扣。",
        ],
        quote: "折扣的力度写在明面上，回收折扣的规则却常常藏在细则里。",
      },
      {
        heading: "阶梯的高层：身份与数据的双重价值",
        paragraphs: [
          "最高等级的会员往往享有最完整的权益，但同时也贡献了最完整的消费数据。对这些用户而言，价格让利只是收益的一部分，数据价值才是更深层的回报。",
          "把三层放在一起看，定价阶梯的真实作用就清晰了：它不是在卖商品，而是在经营用户关系的生命周期。",
        ],
      },
    ],
  },
  {
    id: 6,
    slug: "hot-search-lifecycle",
    title: "热搜的诞生与消散：一次公共讨论的完整生命周期",
    categoryId: "opinion",
    excerpt:
      "一个话题从进入榜单到彻底沉底，通常只有四十小时左右。我们以一次公共讨论为例，记录它在每个阶段的样子。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20editorial%20image%20of%20a%20lone%20warm%20amber%20glowing%20dot%20expanding%20into%20ripples%20over%20a%20dark%20ink%20textured%20surface%2C%20minimal%20composition%2C%20deep%20shadows%2C%20artistic&width=1200&height=750&seq=kl-cover-hotsearch&orientation=landscape",
    author: "看料档案 · 舆情组",
    date: "2026-07-14",
    readTime: 7,
    tags: ["公共讨论", "话题生命周期", "传播节奏"],
    hot: 5340,
    lead:
      "热搜不制造事件，它只是把事件推到更多人面前。但推的方式、推的时长、推的角度，会决定这场讨论最终留下什么。",
    takeaways: [
      "话题的上升期通常短于下降期，速度并不对称。",
      "讨论的质量峰值，往往出现在榜单排名开始下滑之后。",
      "一个话题真正的终点，是它不再被新的信息补充。",
    ],
    sections: [
      {
        heading: "上升期：速度决定第一印象",
        paragraphs: [
          "话题进入榜单的初期，主导权在最早的传播者手里。这个阶段决定了大多数人对事件的第一印象，而第一印象一旦形成，后续的纠错成本会非常高。",
          "因此上升期的关键信息如果缺位，后面再补，效果也会大打折扣。",
        ],
      },
      {
        heading: "平台期：讨论开始分层",
        paragraphs: [
          "进入榜单中段后，讨论者会自然分成几类：关注事实的、表达立场的、参与玩梗的。三者的比例，决定了这场讨论的最终气质。",
          "值得注意的是，话题的质量峰值往往不在排名最高时，而在排名下滑、核心讨论者开始沉淀观点之后。",
        ],
        quote: "热度最高的时刻，通常是信息最混乱的时刻。",
      },
      {
        heading: "消散期：真正的终点",
        paragraphs: [
          "话题从榜单退出并不意味着讨论结束。真正的终点，是它不再被新的信息补充，只剩下重复的观点。",
          "理解这条生命周期曲线，能帮我们在热度最高时保持一点耐心，也让我们知道：讨论的价值，往往在喧嚣散去之后才浮现。",
        ],
      },
    ],
  },
  {
    id: 7,
    slug: "platform-rule-chain-reaction",
    title: "规则微调引发的连锁反应：平台治理里的小改动与大影响",
    categoryId: "industry",
    excerpt:
      "一条只有两行字的规则修订，为什么会让整个细分品类在一周内重新洗牌？我们从治理工具的层面理解这种连锁效应。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20minimal%20editorial%20image%20of%20interlocking%20warm%20bronze%20gears%20and%20thin%20connecting%20lines%20on%20a%20cream%20background%2C%20soft%20shadow%2C%20refined%20industrial%20aesthetic&width=1200&height=750&seq=kl-cover-rule&orientation=landscape",
    author: "看料档案 · 行业组",
    date: "2026-07-05",
    readTime: 9,
    tags: ["平台治理", "规则修订", "品类洗牌"],
    hot: 4880,
    lead:
      "规则文本的变化通常很小，但它作用在成千上万个经营主体上时，产生的影响会被成倍放大。这篇文章关注的是这种放大机制本身。",
    takeaways: [
      "规则的影响不取决于文本长度，而取决于它触及了多少个存量主体。",
      "最先做出反应的，通常是处于边缘位置的小型经营者。",
      "连锁反应的最终结果，往往与修订的初衷存在偏差。",
    ],
    sections: [
      {
        heading: "一条规则的三种解读",
        paragraphs: [
          "同一句规则文本，在不同规模的经营者眼里含义并不相同。头部经营者会按最严格的合规标准执行，边缘经营者则会先试探执行边界。",
          "这种解读差异，是连锁反应的第一块多米诺骨牌。",
        ],
      },
      {
        heading: "被放大的执行偏差",
        paragraphs: [
          "当平台开始根据新规则调整分发与推荐时，执行层面的细微差异会被系统不断放大。一次轻微的降权，可能意味着一个品类彻底失去曝光。",
          "这也解释了为什么同一套规则，落在不同经营者身上，后果会相差如此悬殊。",
        ],
        quote: "规则是统一的，但承受规则的能力从来不是。",
      },
      {
        heading: "偏差之外：治理的长期课题",
        paragraphs: [
          "复盘这次调整，最有价值的发现不是「谁受影响最大」，而是规则设计与执行之间天然存在的差距。",
          "对平台而言，如何在修订前预估到边缘主体的承受能力，是比规则本身更难的课题。这也是所有平台治理都会反复面对的问题。",
        ],
      },
    ],
  },
  {
    id: 8,
    slug: "brand-reputation-turning-points",
    title: "品牌口碑反转的三次关键节点：一次舆论转向的细节复盘",
    categoryId: "business",
    excerpt:
      "一个被广泛好评的品牌，是如何在半年内经历口碑反转的？我们把过程切成三个节点，每个节点都对应一次具体的选择。",
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20editorial%20image%20of%20a%20warm%20amber%20mirror%20reflecting%20distorted%20geometric%20shapes%20on%20muted%20clay%20background%2C%20minimal%20artistic%20concept%2C%20soft%20grain%20texture&width=1200&height=750&seq=kl-cover-reputation&orientation=landscape",
    author: "看料档案 · 商业组",
    date: "2026-06-26",
    readTime: 10,
    tags: ["品牌口碑", "舆论转向", "危机沟通"],
    hot: 4120,
    lead:
      "口碑反转很少由单一事件造成。它更像三次选择叠加后的结果，每次选择单独看都不算致命，但连在一起，就改变了公众的判断。",
    takeaways: [
      "第一次转折往往被误判为「偶然的负面反馈」。",
      "第二次转折的关键，是回应速度与回应内容是否匹配。",
      "第三次转折之后，品牌实际上失去了重新定义议题的主动权。",
    ],
    sections: [
      {
        heading: "第一次转折：被忽略的小范围反馈",
        paragraphs: [
          "反转的起点通常很小。一批用户的集中反馈，在当时被视为孤立的客服问题，没有被上升到产品或策略层面处理。",
          "这个阶段的判断失误，往往不是能力问题，而是注意力分配问题。",
        ],
      },
      {
        heading: "第二次转折：错位的回应",
        paragraphs: [
          "当反馈扩散成公开讨论后，品牌通常会做出回应。但如果回应的是「态度」而非「问题本身」，就会产生错位感，反而扩大争议。",
          "有效的回应需要同时解决事实层面与情绪层面，缺一都会留下新的追问。",
        ],
        quote: "公众要的常常不是道歉，而是一个说得通的解释。",
      },
      {
        heading: "第三次转折：议题主导权的转移",
        paragraphs: [
          "到了第三个节点，讨论的内容已经不再由品牌决定。此时无论说什么，都会被放进既有的框架里解读。",
          "这也是口碑反转最值得记录的地方：真正改变局面的，往往是最初那几个看似微不足道的选择。把细节看清楚，才能在下一个节点到来前做出不同的决定。",
        ],
      },
    ],
  },
];

export const categoryNameMap: Record<string, string> = {
  business: "商业起底",
  industry: "行业内幕",
  opinion: "舆情深挖",
};